import { useState, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Title3,
  Body1,
  Body2,
  Caption1,
  Badge,
  Button,
  Card,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  Input,
  Dropdown,
  Option,
  Field,
  Spinner,
  MessageBar,
  MessageBarBody,
  Textarea,
  Link,
  Divider,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import {
  ArrowSync24Regular,
  Link24Regular,
  DocumentArrowUpRegular,
  CloudSync24Regular,
  Search24Regular,
} from '@fluentui/react-icons';
import {
  INTEGRATION_PROVIDERS,
  type IntegrationProvider,
  type IntegrationSyncResult,
  type CloudSpendRecord,
} from '@/models/integrations';
import type { Application } from '@/models/types';
import { useSharePointList } from '@/hooks/useSharePointList';
import { useAppContext } from '@/store/appContext';
import { canSyncData } from '@/utils/permissions';
import { AccessDenied } from '@/components/AccessDenied';
import {
  syncExternalIntegration,
  peekIntegrationTiers,
} from '@/graph/integrationService';
import {
  fetchAzureUsage,
  fetchTwilioUsage,
  parseGcpBillingCsv,
  saveCloudSpend,
} from '@/graph/usageService';
import { useMsal } from '@azure/msal-react';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '24px' },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
    },
  },
  dateGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
    minHeight: '200px',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  cardBody: { flex: 1 },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  unmatchedList: {
    maxHeight: '200px',
    overflowY: 'auto' as const,
    padding: '8px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: '12px',
    fontFamily: 'monospace',
  },
  tierTable: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
    },
  },
  tierRow: {
    display: 'contents',
  },
});

export function Integrations() {
  const styles = useStyles();
  const { graphClient, currentUser, listIds } = useAppContext();
  const { instance, accounts } = useMsal();
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (currentUser && !canSyncData(currentUser.role)) {
    return <AccessDenied reason="Integrations page is restricted to Administrators only." />;
  }

  const { items: apps } = useSharePointList<Application>('OC_Applications', {
    autoFetch: !!listIds,
  });

  const [activeProvider, setActiveProvider] =
    useState<IntegrationProvider | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [orgId, setOrgId] = useState('');
  const [csvData, setCsvData] = useState('');
  const [defaultApp, setDefaultApp] = useState('');
  const [tierMap, setTierMap] = useState<Record<string, string>>({});
  const [detectedTiers, setDetectedTiers] = useState<string[]>([]);
  const [peeking, setPeeking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState<IntegrationSyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [spendPeriod, setSpendPeriod] = useState<{ start: string; end: string }>({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const [subId, setSubId] = useState('');
  const [cloudResult, setCloudResult] = useState<string | null>(null);

  const openProvider = (provider: IntegrationProvider) => {
    setActiveProvider(provider);
    setApiKey('');
    setOrgId('');
    setCsvData('');
    setDefaultApp('');
    setTierMap({});
    setDetectedTiers([]);
    setResult(null);
    setError(null);
    setCloudResult(null);
    setSubId('');
  };

  const handleCsvFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => setCsvData(e.target?.result as string);
    reader.readAsText(file);
  };

  // ─── Peek: fetch source to discover tiers ──────────────────────────────────
  const handlePeek = async () => {
    if (!activeProvider) return;
    setPeeking(true);
    setError(null);
    try {
      const res = await peekIntegrationTiers(activeProvider, {
        apiKey,
        organizationId: orgId,
        csvData,
      });
      if (res.error) {
        setError(res.error);
      } else {
        setDetectedTiers(res.tiers);
      }
    } finally {
      setPeeking(false);
    }
  };

  // ─── Seat sync ─────────────────────────────────────────────────────────────
  const handleSync = async () => {
    if (!graphClient || !activeProvider || !defaultApp) return;
    setSyncing(true);
    setError(null);
    try {
      const res = await syncExternalIntegration(
        graphClient,
        activeProvider,
        {
          apiKey,
          organizationId: orgId,
          csvData,
          tierMapping: Object.keys(tierMap).length > 0 ? tierMap : undefined,
        },
        defaultApp,
        currentUser?.email ?? '',
      );
      setResult(res);
      if (res.error) setError(res.error);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // ─── Cloud usage sync ──────────────────────────────────────────────────────
  const handleAzureSync = async () => {
    if (!graphClient || accounts.length === 0 || !subId) return;
    setSyncing(true);
    setError(null);
    try {
      // Get Azure ARM token (separate scope from Graph)
      const tokenResp = await instance.acquireTokenSilent({
        scopes: ['https://management.azure.com/user_impersonation'],
        account: accounts[0],
      });
      const records = await fetchAzureUsage(
        tokenResp.accessToken,
        subId,
        spendPeriod.start,
        spendPeriod.end,
      );
      const res = await saveCloudSpend(
        graphClient,
        records,
        currentUser?.email ?? '',
      );
      setCloudResult(`${res.saved} new, ${res.updated} updated · Total £${res.total.toFixed(2)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Azure sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleTwilioSync = async () => {
    if (!graphClient || !apiKey || !orgId) return;
    setSyncing(true);
    setError(null);
    try {
      const records = await fetchTwilioUsage(
        orgId, // Twilio AccountSid
        apiKey, // Twilio AuthToken
        spendPeriod.start,
        spendPeriod.end,
      );
      const res = await saveCloudSpend(
        graphClient,
        records,
        currentUser?.email ?? '',
      );
      setCloudResult(`Twilio: ${res.saved} new, ${res.updated} updated · Total ${res.total.toFixed(2)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Twilio sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleGcpUpload = async () => {
    if (!graphClient || !csvData) return;
    setSyncing(true);
    setError(null);
    try {
      const records: CloudSpendRecord[] = parseGcpBillingCsv(csvData);
      const res = await saveCloudSpend(
        graphClient,
        records,
        currentUser?.email ?? '',
      );
      setCloudResult(`GCP: ${res.saved} new, ${res.updated} updated · Total ${res.total.toFixed(2)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'GCP import failed');
    } finally {
      setSyncing(false);
    }
  };

  const allProviders = Object.values(INTEGRATION_PROVIDERS);
  const seatProviders = allProviders.filter((p) => p.kind === 'seat');
  const usageProviders = allProviders.filter((p) => p.kind === 'usage');

  const provDef = activeProvider ? INTEGRATION_PROVIDERS[activeProvider] : null;
  const isUsage = provDef?.kind === 'usage';

  return (
    <div className={styles.page}>
      <PageTitle>External Integrations</PageTitle>
      <Caption1>
        Connect third-party tools for automatic license sync. Seat-based integrations
        map users to licenses; usage-based integrations track cloud consumption spend.
      </Caption1>

      {/* ── Seat-based ─────────────────────────────────────────── */}
      <div className={styles.sectionHeader}>
        <Title3>Seat-based (per-user licenses)</Title3>
      </div>
      <div className={styles.grid}>
        {seatProviders.map((p) => (
          <Card key={p.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <div>
                <Body1 style={{ fontWeight: 600 }}>{p.displayName}</Body1>
                <Caption1>
                  {p.vendor} · {p.category}
                </Caption1>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                <Badge
                  appearance={p.hasApi ? 'filled' : 'tint'}
                  color={p.hasApi ? 'brand' : 'informative'}
                  size="small"
                >
                  {p.hasApi ? 'API' : 'CSV'}
                </Badge>
                {p.supportsTiers && (
                  <Badge appearance="outline" color="warning" size="small">
                    Multi-tier
                  </Badge>
                )}
              </div>
            </div>
            <Body2 className={styles.cardBody}>{p.description}</Body2>
            <Button
              appearance="primary"
              icon={p.hasApi ? <ArrowSync24Regular /> : <DocumentArrowUpRegular />}
              onClick={() => openProvider(p.id)}
            >
              {p.hasApi ? 'Sync via API' : 'Upload CSV'}
            </Button>
          </Card>
        ))}
      </div>

      <Divider />

      {/* ── Usage-based ────────────────────────────────────────── */}
      <div className={styles.sectionHeader}>
        <Title3>Usage-based (cloud consumption)</Title3>
      </div>
      <div className={styles.grid}>
        {usageProviders.map((p) => (
          <Card key={p.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <div>
                <Body1 style={{ fontWeight: 600 }}>{p.displayName}</Body1>
                <Caption1>
                  {p.vendor} · {p.category}
                </Caption1>
              </div>
              <Badge appearance="tint" color="severe" size="small">
                Spend
              </Badge>
            </div>
            <Body2 className={styles.cardBody}>{p.description}</Body2>
            <Button
              appearance="primary"
              icon={<CloudSync24Regular />}
              onClick={() => openProvider(p.id)}
            >
              {p.hasApi ? 'Pull Usage' : 'Upload Billing CSV'}
            </Button>
          </Card>
        ))}
      </div>

      {/* ── Dialog: Seat sync ─────────────────────────────────── */}
      <Dialog open={!!activeProvider} onOpenChange={() => setActiveProvider(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {isUsage ? 'Pull cloud spend from ' : 'Sync '}{provDef?.displayName}
            </DialogTitle>
            <DialogContent>
              {error && (
                <MessageBar intent="error" style={{ marginBottom: 12 }}>
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}
              {result && !error && (
                <MessageBar intent="success" style={{ marginBottom: 12 }}>
                  <MessageBarBody>
                    {result.usersFound} users · {result.assigned} assigned · {result.revoked} revoked · {result.unmatched.length} unmatched
                  </MessageBarBody>
                </MessageBar>
              )}
              {cloudResult && (
                <MessageBar intent="success" style={{ marginBottom: 12 }}>
                  <MessageBarBody>{cloudResult}</MessageBarBody>
                </MessageBar>
              )}

              <div className={styles.form}>
                {/* Usage-based flow */}
                {isUsage ? (
                  <>
                    {activeProvider === 'AzureUsage' && (
                      <>
                        <Caption1>
                          This uses your Azure AD token to pull Cost Management data.
                          You need the <strong>Cost Management Reader</strong> role
                          on the subscription.
                        </Caption1>
                        <Field label="Azure Subscription ID" required>
                          <Input
                            value={subId}
                            onChange={(_, d) => setSubId(d.value)}
                            placeholder="00000000-0000-0000-0000-000000000000"
                          />
                        </Field>
                      </>
                    )}
                    {activeProvider === 'TwilioUsage' && (
                      <>
                        <Field label="Account SID" required>
                          <Input
                            value={orgId}
                            onChange={(_, d) => setOrgId(d.value)}
                            placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          />
                        </Field>
                        <Field label="Auth Token" required>
                          <Input
                            type="password"
                            value={apiKey}
                            onChange={(_, d) => setApiKey(d.value)}
                          />
                        </Field>
                      </>
                    )}
                    {activeProvider === 'GCPUsage' && (
                      <>
                        <Caption1>
                          Export your BigQuery billing data to CSV. Columns:
                          service, cost, currency, invoice_month, project_id.
                        </Caption1>
                        <Field label="Billing CSV" required>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleCsvFile(f);
                            }}
                            style={{ display: 'none' }}
                          />
                          <Button
                            appearance="outline"
                            icon={<DocumentArrowUpRegular />}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            {csvData ? 'CSV loaded — re-select' : 'Select CSV file'}
                          </Button>
                        </Field>
                      </>
                    )}
                    {activeProvider !== 'GCPUsage' && (
                      <div className={styles.dateGrid}>
                        <Field label="Period Start">
                          <Input
                            type="date"
                            value={spendPeriod.start}
                            onChange={(_, d) => setSpendPeriod((p) => ({ ...p, start: d.value }))}
                          />
                        </Field>
                        <Field label="Period End">
                          <Input
                            type="date"
                            value={spendPeriod.end}
                            onChange={(_, d) => setSpendPeriod((p) => ({ ...p, end: d.value }))}
                          />
                        </Field>
                      </div>
                    )}
                  </>
                ) : (
                  /* Seat-based flow */
                  <>
                    <Field label="Default Application (for unmapped tiers)" required>
                      <Dropdown
                        placeholder="Select default Prism application"
                        value={
                          apps.find((a) => a.id === defaultApp)?.fields.Title ?? ''
                        }
                        onOptionSelect={(_, d) => setDefaultApp(d.optionValue as string)}
                      >
                        {apps.map((a) => (
                          <Option key={a.id} value={a.id}>
                            {a.fields.Title}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>

                    {provDef?.hasApi ? (
                      <>
                        <Field label="API Token / PAT" required>
                          <Input
                            type="password"
                            value={apiKey}
                            onChange={(_, d) => setApiKey(d.value)}
                            placeholder="Paste your personal access token"
                          />
                        </Field>
                        {provDef.tokenHelpUrl && (
                          <Caption1>
                            <Link24Regular style={{ verticalAlign: 'middle', fontSize: 14 }} />
                            <Link href={provDef.tokenHelpUrl} target="_blank">
                              {' '}Where to get the token
                            </Link>
                          </Caption1>
                        )}
                        {['AzureDevOps', 'GitHub', 'Figma', 'Claude', 'Snyk'].includes(
                          activeProvider ?? '',
                        ) && (
                          <Field
                            label={
                              activeProvider === 'AzureDevOps'
                                ? 'ADO Organization name'
                                : activeProvider === 'GitHub'
                                  ? 'GitHub Organization'
                                  : activeProvider === 'Figma'
                                    ? 'Figma Team ID'
                                    : activeProvider === 'Claude'
                                      ? 'Anthropic Organization ID'
                                      : 'Snyk Org ID (UUID)'
                            }
                            required
                          >
                            <Input
                              value={orgId}
                              onChange={(_, d) => setOrgId(d.value)}
                            />
                          </Field>
                        )}
                      </>
                    ) : (
                      <>
                        <Field
                          label="Upload CSV (columns: email, name, role, seatType)"
                          required
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleCsvFile(f);
                            }}
                            style={{ display: 'none' }}
                          />
                          <Button
                            appearance="outline"
                            icon={<DocumentArrowUpRegular />}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            {csvData ? 'CSV loaded — re-select' : 'Select CSV file'}
                          </Button>
                        </Field>
                        {csvData && (
                          <Textarea
                            value={csvData}
                            onChange={(_, d) => setCsvData(d.value)}
                            rows={6}
                            resize="vertical"
                          />
                        )}
                      </>
                    )}

                    {/* Multi-tier mapping */}
                    {provDef?.supportsTiers && (
                      <>
                        <Divider />
                        <Field label="Tier Mapping (optional)">
                          <Caption1>
                            Different tiers of {provDef.displayName} (e.g., ADO Basic
                            vs VS Enterprise) often have different costs. Click{' '}
                            <strong>Detect Tiers</strong> to fetch them from the source,
                            then map each to a specific application row.
                          </Caption1>
                        </Field>
                        <Button
                          appearance="outline"
                          icon={peeking ? <Spinner size="tiny" /> : <Search24Regular />}
                          onClick={handlePeek}
                          disabled={peeking || (provDef.hasApi ? !apiKey : !csvData)}
                        >
                          {peeking ? 'Detecting...' : 'Detect Tiers'}
                        </Button>
                        {detectedTiers.length > 0 && (
                          <div className={styles.tierTable}>
                            <Body2 style={{ fontWeight: 600 }}>Source Tier</Body2>
                            <Body2 style={{ fontWeight: 600 }}>→ Prism Application</Body2>
                            {detectedTiers.map((tier) => (
                              <div key={tier} className={styles.tierRow}>
                                <Body2>{tier}</Body2>
                                <Dropdown
                                  placeholder="Map to application"
                                  value={
                                    apps.find((a) => a.id === tierMap[tier])?.fields
                                      .Title ?? ''
                                  }
                                  onOptionSelect={(_, d) =>
                                    setTierMap((prev) => ({
                                      ...prev,
                                      [tier]: d.optionValue as string,
                                    }))
                                  }
                                  size="small"
                                >
                                  {apps.map((a) => (
                                    <Option key={a.id} value={a.id}>
                                      {a.fields.Title}
                                    </Option>
                                  ))}
                                </Dropdown>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {result && result.unmatched.length > 0 && (
                      <Field label="Unmatched emails (sync users first)">
                        <div className={styles.unmatchedList}>
                          {result.unmatched.map((e) => (
                            <div key={e}>{e}</div>
                          ))}
                        </div>
                      </Field>
                    )}
                  </>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Close</Button>
              </DialogTrigger>
              {isUsage ? (
                <Button
                  appearance="primary"
                  onClick={
                    activeProvider === 'AzureUsage'
                      ? handleAzureSync
                      : activeProvider === 'TwilioUsage'
                        ? handleTwilioSync
                        : handleGcpUpload
                  }
                  disabled={syncing}
                  icon={syncing ? <Spinner size="tiny" /> : <CloudSync24Regular />}
                >
                  {syncing ? 'Syncing...' : 'Import Spend'}
                </Button>
              ) : (
                <Button
                  appearance="primary"
                  onClick={handleSync}
                  disabled={
                    syncing ||
                    !defaultApp ||
                    (provDef?.hasApi ? !apiKey : !csvData)
                  }
                  icon={syncing ? <Spinner size="tiny" /> : <ArrowSync24Regular />}
                >
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </Button>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
